import React from 'react';
import {
  Card, CardContent, Typography, Box, Chip, Link, Tooltip
} from '@mui/material';
import {
  Storage, Public, AccessTime, NetworkCheck, Link as LinkIcon,
  NotificationsActive, NotificationsOff
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { IconButton } from '@mui/material';
import { GlassmorphicCard, CardTitle, BodyText, getStatusChipStyles } from '../styles/sharedStyles';

const StyledCard = styled(GlassmorphicCard, {
  shouldForwardProp: (prop) => prop !== 'isOnline'
})(({ isOnline }) => ({
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-5px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
  },
}));

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'isOnline'
})(({ isOnline }) => getStatusChipStyles(isOnline ? 'running' : 'stopped'));

const ServiceIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOnline'
})(({ isOnline }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '16px',
  background: isOnline
    ? 'linear-gradient(135deg, rgba(0, 255, 136, 0.1), rgba(0, 212, 170, 0.1))'
    : 'linear-gradient(135deg, rgba(255, 68, 68, 0.1), rgba(255, 107, 107, 0.1))',
  border: `1px solid ${isOnline ? 'rgba(0, 255, 136, 0.3)' : 'rgba(255, 68, 68, 0.3)'}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.3s ease',
  boxShadow: isOnline
    ? '0 4px 16px rgba(0, 255, 136, 0.1)'
    : '0 4px 16px rgba(255, 68, 68, 0.1)',
}));

const SERVICE_ICONS = {
  immich: '/immich.png',
  jellyfin: '/jellyfin.png',
  nextcloud: '/nextcloud.png',
  webdav: '/webdav.png',
  ddns_updater: '/ddns-updater.png',
  twofactor_auth: '/2fauth-light.png',
  gramps: '/gramps-web.png',
  home_assistant: '/home-assistant-alt.png',
  dockge: '/dockge.png',
  cockpit: '/cockpit-light.png',
  adguard: '/adguard-home.png',
  nginx_proxy_manager: '/nginx-proxy-manager.png',
  truenas: '/truenas_logo.png',
  qbit: '/qbittorrent.png'
};

const getServiceIcon = (service) => {
  if (!service) return null;
  const key = (service.name || service.display_name || '').toLowerCase();
  return SERVICE_ICONS[key];
};

const StyledLink = styled(Link)({
  color: '#00d4ff',
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: '0.875rem',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  transition: 'all 0.3s ease',
  position: 'relative',
  zIndex: 2,
  '&:hover': {
    color: '#00b8e6',
    textDecoration: 'underline',
    transform: 'translateX(2px)',
  },
});

const ServiceCard = ({ service, notificationsEnabled = true, onToggleNotifications }) => {
  const theme = useTheme();
  // Defensive check for service object
  if (!service) {
    console.error('ServiceCard: service prop is null or undefined');
    return null;
  }
  // Helper function to format dates in Warsaw timezone (UTC+1 in winter)
  const formatDateInWarsaw = (dateString, options = {}) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      // Warsaw is UTC+1 in winter (CET), add 1 hour
      const warsawDate = new Date(date.getTime() + (1 * 60 * 60 * 1000));
      // Format as local time (which will now be Warsaw time)
      return warsawDate.toLocaleString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(',', '');
    } catch (error) {
      console.error('Error formatting date in Warsaw timezone:', error);
      return dateString;
    }
  };

  // Helper function to adjust dates for Warsaw timezone (UTC+1 in winter)
  const adjustForWarsawTimezone = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      // Warsaw is UTC+1 in winter (CET), add 1 hour
      return new Date(date.getTime() + (1 * 60 * 60 * 1000));
    } catch (error) {
      console.error('Error adjusting date for Warsaw timezone:', error);
      return new Date(dateString);
    }
  };

  const formatDistanceToNow = (date, options = {}) => {
    const now = new Date();
    const diff = now - adjustForWarsawTimezone(date);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    return 'just now';
  };

  return (
    <StyledCard isOnline={service.is_online || false}>
      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <ServiceIcon isOnline={service.is_online || false}>
              {getServiceIcon(service) ? (
                <Box
                  component="img"
                  src={getServiceIcon(service)}
                  alt={service.display_name || service.name || 'service icon'}
                  sx={{ width: 28, height: 28, objectFit: 'contain' }}
                />
              ) : (
                <Storage sx={{
                  fontSize: 24,
                  color: (service.is_online !== false) ? '#00ff88' : '#ff4444'
                }} />
              )}
            </ServiceIcon>
            <Box>
              <Typography variant="h6" sx={{
                fontWeight: 600,
                fontSize: '1rem',
                color: '#ffffff',
                mb: 0.5
              }}>
                {service.display_name || service.name || 'Unknown Service'}
              </Typography>
              {service.is_public && (
                <Tooltip title="Public Service">
                  <Public sx={{
                    fontSize: 14,
                    color: '#00d4ff',
                    opacity: 0.8
                  }} />
                </Tooltip>
              )}
            </Box>
          </Box>
          <Box display="flex" alignItems="center">
            <StatusChip
              label={service.is_online ? "Online" : "Offline"}
              isOnline={service.is_online || false}
              size="small"
            />
            {onToggleNotifications && (
              <Tooltip title={notificationsEnabled ? "Notifications Enabled" : "Notifications Disabled"}>
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.preventDefault();
                    onToggleNotifications(service.name, !notificationsEnabled);
                  }}
                  sx={{ 
                    ml: 1,
                    color: notificationsEnabled ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.3)',
                    background: notificationsEnabled ? 'rgba(0, 212, 255, 0.05)' : 'rgba(255, 255, 255, 0.03)',
                    '&:hover': {
                      background: notificationsEnabled ? 'rgba(0, 212, 255, 0.1)' : 'rgba(255, 255, 255, 0.08)',
                    }
                  }}
                >
                  {notificationsEnabled ? (
                    <NotificationsActive sx={{ fontSize: 16 }} />
                  ) : (
                    <NotificationsOff sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1 }}>
          {service.url ? (
            <StyledLink
              href={service.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <LinkIcon sx={{ fontSize: 16 }} />
              {service.url.replace(/^https?:\/\//, '')}
            </StyledLink>
          ) : (
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              No URL available
            </Typography>
          )}

          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {service.response_time && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '8px',
                px: 1.5,
                py: 0.5
              }}>
                <NetworkCheck sx={{ fontSize: 14, color: '#00ff88', mr: 0.5 }} />
                <Typography variant="body2" sx={{ color: '#00ff88', fontSize: '0.75rem' }}>
                  {typeof service.response_time === 'number' ? service.response_time.toFixed(0) : service.response_time}ms
                </Typography>
              </Box>
            )}

            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              background: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '8px',
              px: 1.5,
              py: 0.5
            }}>
              <AccessTime sx={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.6)', mr: 0.5 }} />
              <Typography variant="body2" sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.75rem'
              }}>
                {service.last_checked
                  ? `${formatDistanceToNow(service.last_checked, { addSuffix: true })} (${formatDateInWarsaw(service.last_checked)})`
                  : 'Never checked'
                }
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default ServiceCard;
